import { z } from 'zod';
import { centavosPositivos, dia, hora, textoRequerido, uuid } from './comunes';

export const tipoComision = z.enum(['porcentaje', 'fijo']);

export const crearServicioSchema = z.object({
  nombre: textoRequerido(120),
  precio: centavosPositivos.refine((v) => v > 0, 'El precio debe ser mayor a cero'),
  categoria: z.string().trim().max(60).nullable().optional(),
  activo: z.boolean().optional(),
  orden: z.number().int().min(0).max(999).optional(),
});
export type CrearServicioInput = z.infer<typeof crearServicioSchema>;
export const actualizarServicioSchema = crearServicioSchema.partial();

/** Configuración de un servicio para un barbero: duración y comisión (porcentaje ×100 o centavos fijos). */
export const servicioBarberoSchema = z
  .object({
    duracionMin: z.number().int().min(5).max(480),
    comisionTipo: tipoComision,
    comisionValor: z.number().int().min(0),
  })
  .refine((v) => v.comisionTipo !== 'porcentaje' || v.comisionValor <= 10_000, {
    message: 'El porcentaje no puede superar 100%',
    path: ['comisionValor'],
  });
export type ServicioBarberoInput = z.infer<typeof servicioBarberoSchema>;

export interface ServicioVista {
  id: string;
  nombre: string;
  precio: number;
  categoria: string | null;
  activo: boolean;
  orden: number;
  barberos: { barberoId: string; duracionMin: number; comisionTipo: 'porcentaje' | 'fijo'; comisionValor: number }[];
}

export const filaHorarioSchema = z
  .object({
    barberoId: uuid,
    dia,
    esDescanso: z.boolean().default(false),
    entrada: hora.optional(),
    salida: hora.optional(),
    comidaInicio: hora.optional(),
    comidaFin: hora.optional(),
  })
  .superRefine((f, ctx) => {
    if (f.esDescanso) return;
    if (!f.entrada || !f.salida) {
      ctx.addIssue({ code: 'custom', message: 'Indica entrada y salida', path: ['entrada'] });
      return;
    }
    if (f.entrada >= f.salida) ctx.addIssue({ code: 'custom', message: 'La entrada debe ser antes de la salida', path: ['salida'] });
    if (!!f.comidaInicio !== !!f.comidaFin) {
      ctx.addIssue({ code: 'custom', message: 'Indica inicio y fin de comida', path: ['comidaFin'] });
    } else if (f.comidaInicio && f.comidaFin) {
      if (f.comidaInicio >= f.comidaFin || f.comidaInicio < f.entrada || f.comidaFin > f.salida) {
        ctx.addIssue({ code: 'custom', message: 'La comida debe estar dentro del horario', path: ['comidaInicio'] });
      }
    }
  });
export type FilaHorarioInput = z.infer<typeof filaHorarioSchema>;

export const guardarSemanaSchema = z.object({
  semana: dia,
  filas: z.array(filaHorarioSchema).max(500),
  solicitarAlDueno: z.boolean().optional(),
});
export type GuardarSemanaInput = z.infer<typeof guardarSemanaSchema>;

export const copiarSemanaSchema = z.object({ desde: dia, hacia: dia });

export const solicitarVacacionSchema = z
  .object({
    barberoId: uuid.optional(),
    desde: dia,
    hasta: dia,
    motivo: z.string().trim().max(300).optional(),
  })
  .refine((v) => v.desde <= v.hasta, { message: 'La fecha final debe ser igual o posterior a la inicial', path: ['hasta'] });
export type SolicitarVacacionInput = z.infer<typeof solicitarVacacionSchema>;

export const resolverVacacionSchema = z.object({ estado: z.enum(['autorizada', 'rechazada']) });

export interface HorarioVista {
  barberoId: string;
  dia: string;
  esDescanso: boolean;
  entrada: string | null;
  salida: string | null;
  comidaInicio: string | null;
  comidaFin: string | null;
}

export interface SemanaHorarios {
  semana: string;
  dias: string[];
  barberos: {
    id: string;
    nombre: string;
    dias: Record<string, { horario: HorarioVista | null; vacacionAutorizada: boolean }>;
  }[];
  pendientes: { dia: string; citaId: string; cliente: string; hora: string; barberoPreferidoId: string | null }[];
}
